import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DialogVariantOptions } from 'shared/assets/styles';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';

import * as Styled from './choice.style';

export type ChoiceDialogProps = {
  open: boolean;
  // opt-in for the caller that raises this from inside a PopupMenu
  abovePopups?: boolean;
  title: string;
  message: string;
  // rendered rightmost, where the eye lands last
  primaryLabel: string;
  secondaryLabel: string;
  // opt-in third answer. Its presence stacks the actions, read primary → tertiary → secondary
  tertiaryLabel?: string;
  cancelLabel?: string;
  // red suits a choice that throws something away; a neutral one would read as a warning
  actionColor?: Colors;
  onPrimary: () => void;
  onSecondary: () => void;
  onTertiary?: () => void;
  onCancel: () => void;
};

// The three-way "this one / that one / never mind" prompt; ConfirmDialog is yes/no only
export const ChoiceDialog: React.FC<ChoiceDialogProps> = ({
  open,
  title,
  message,
  primaryLabel,
  secondaryLabel,
  tertiaryLabel,
  cancelLabel,
  actionColor = Colors.Error,
  onPrimary,
  onSecondary,
  onTertiary,
  onCancel,
  abovePopups,
}) => {
  const { t } = useTranslation();

  const tertiary =
    tertiaryLabel && onTertiary
      ? { label: tertiaryLabel, onClick: onTertiary }
      : null;

  // Stacked reads top-down from the narrowest answer; a plain row keeps the primary one rightmost.
  const actions = tertiary
    ? [
        { label: primaryLabel, onClick: onPrimary },
        tertiary,
        { label: secondaryLabel, onClick: onSecondary },
      ]
    : [
        { label: secondaryLabel, onClick: onSecondary },
        { label: primaryLabel, onClick: onPrimary },
      ];

  return (
    <Styled.Dialog
      $abovePopups={abovePopups}
      open={open}
      onClose={onCancel}
      variant={DialogVariantOptions.ExtraSmall}
    >
      <Styled.Header>
        <Typography variant="h5" component="h1">
          {title}
        </Typography>
      </Styled.Header>

      <Styled.Body>
        <Typography variant="body2">{message}</Typography>
      </Styled.Body>

      <Styled.Footer $stacked={!!tertiary}>
        <Button
          variant={Variants.Contained}
          size={Sizes.Medium}
          color={Colors.TextPrimary}
          onClick={onCancel}
        >
          {cancelLabel ?? t('general.cancel')}
        </Button>

        <Styled.Interactions $stacked={!!tertiary}>
          {actions.map(({ label, onClick }) => (
            <Button
              key={label}
              variant={Variants.Contained}
              size={Sizes.Medium}
              color={actionColor}
              onClick={onClick}
            >
              {label}
            </Button>
          ))}
        </Styled.Interactions>
      </Styled.Footer>
    </Styled.Dialog>
  );
};
